import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import { X, Send, Trash2, Video, Clock, Reply, AlertCircle, Edit3 } from 'lucide-react';


const DeliverableViewModal = ({ deliverableId, onClose, currentUser, onUpdate }) => {
  const [deliverable, setDeliverable] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const videoRef = useRef(null);

  useEffect(() => {
    fetchDeliverableView();
  }, [deliverableId]);

  const fetchDeliverableView = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await api.get(
        `/deliverables/${deliverableId}/view`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.data.success) {
        setDeliverable(response.data.deliverable);
        setComments(response.data.comments);
      }
    } catch (error) {
      console.error('Error fetching deliverable:', error);
      alert('Failed to load deliverable');
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      setSubmitting(true);
      const token = localStorage.getItem('access_token');
      
      // Get video timestamp if this is a video and it's paused
      let timestamp = null;
      if (videoRef.current && deliverable.deliverable_type === 'file') {
        timestamp = videoRef.current.currentTime;
      }

      await api.post(
        `/deliverables/${deliverableId}/comments`,
        {
          content: newComment,
          parent_comment_id: replyingTo,
          video_timestamp: timestamp
        },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      setNewComment('');
      setReplyingTo(null);
      await fetchDeliverableView();
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;

    try {
      const token = localStorage.getItem('access_token');
      await api.delete(
        `/deliverables/${deliverableId}/comments/${commentId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      await fetchDeliverableView();
    } catch (error) {
      console.error('Error deleting comment:', error);
      alert(error.response?.data?.detail || 'Failed to delete comment');
    }
  };

  const handleClearAllComments = async () => {
    if (!window.confirm('Clear ALL comments on this deliverable? This cannot be undone.')) return;

    try {
      const token = localStorage.getItem('access_token');
      await api.delete(
        `/deliverables/${deliverableId}/comments`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      await fetchDeliverableView();
    } catch (error) {
      console.error('Error clearing comments:', error);
      alert(error.response?.data?.detail || 'Failed to clear comments');
    }
  };


  const handleStartEditTitle = () => {
    setEditedTitle(deliverable.title);
    setEditingTitle(true);
  };

  const handleSaveTitle = async () => {
    if (!editedTitle.trim()) {
      alert('Title cannot be empty');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      await api.put(
        `/deliverables/${deliverableId}`,
        { title: editedTitle.trim() },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      setDeliverable({ ...deliverable, title: editedTitle.trim() });
      setEditingTitle(false);
      
      // Notify parent to refresh deliverables list
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error updating title:', error);
      alert('Failed to update title');
    }
  };

  const handleCancelEditTitle = () => {
    setEditingTitle(false);
    setEditedTitle('');
  };


  const handleTimestampClick = (seconds) => {
    if (videoRef.current) {
      videoRef.current.currentTime = seconds;
      videoRef.current.play();
    }
  };

  const formatTimestamp = (seconds) => {
    if (!seconds && seconds !== 0) return null;
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const renderComment = (comment, depth = 0) => {
    const isOwnComment = comment.user_id === currentUser?.id;
    const canDelete = isOwnComment || ['owner', 'admin', 'project_manager'].includes(currentUser?.role);
    
    // Check if this is a client comment (user_name contains @ symbol = email)
    const isClientComment = comment.user_name && comment.user_name.includes('@');

    return (
      <div key={comment.id} className={`${depth > 0 ? 'ml-8 mt-1.5' : 'mb-2'}`}>
        <div className="bg-white rounded-lg p-2.5 shadow-sm border border-gray-200">
          <div className="flex items-start justify-between mb-1.5">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className={`text-sm font-semibold ${isClientComment ? 'text-blue-600' : 'text-purple-600'}`}>
                {comment.user_name}
              </span>
              {comment.video_timestamp != null && (
                <>
                  <span className="text-xs text-gray-400">•</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTimestampClick(comment.video_timestamp);
                    }}
                    className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                  >
                    <Clock className="w-3 h-3" />
                    {formatTimestamp(comment.video_timestamp)}
                  </button>
                </>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setReplyingTo(comment.id);
                }}
                className="text-xs text-gray-500 hover:text-purple-600 flex items-center gap-1 px-2 py-1 rounded hover:bg-purple-50"
              >
                <Reply className="w-3 h-3" />
                Reply
              </button>
              {canDelete && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteComment(comment.id);
                  }}
                  className="text-xs text-gray-500 hover:text-red-600 flex items-center gap-1 px-2 py-1 rounded hover:bg-red-50"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
          <p className="text-sm text-gray-800 whitespace-pre-wrap">{comment.content}</p>
        </div>

        {/* Render replies */}
        {comment.replies && comment.replies.length > 0 && (
          <div className="mt-1.5">
            {comment.replies.map(reply => renderComment(reply, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!deliverable) return null;

  // Determine if this is a video or image
  const isVideo = deliverable.file_url && (
    deliverable.file_url.includes('.mp4') ||
    deliverable.file_url.includes('.mov') ||
    deliverable.file_url.includes('.avi') ||
    deliverable.file_url.includes('.webm')
  );

  const isImage = deliverable.file_url && (
    deliverable.file_url.includes('.jpg') ||
    deliverable.file_url.includes('.jpeg') ||
    deliverable.file_url.includes('.png') ||
    deliverable.file_url.includes('.gif') ||
    deliverable.file_url.includes('.webp')
  );

  // Use wider modal for media content
  const isMediaContent = isVideo || isImage;
  const modalWidth = isMediaContent ? 'w-[95vw]' : 'w-full max-w-3xl';

  const canClearAll = ['owner', 'admin', 'project_manager'].includes(currentUser?.role);

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.stopPropagation()}
    >
      <div 
        className={`bg-white rounded-lg shadow-2xl ${modalWidth} max-h-[90vh] flex flex-col`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex-1 mr-4">
            {editingTitle ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveTitle();
                    if (e.key === 'Escape') handleCancelEditTitle();
                  }}
                  className="text-lg font-semibold text-gray-900 border border-purple-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-purple-500 flex-1"
                  autoFocus
                />
                <button
                  onClick={handleSaveTitle}
                  className="px-3 py-1 bg-purple-600 text-white text-sm rounded hover:bg-purple-700 transition-colors"
                >
                  Save
                </button>
                <button
                  onClick={handleCancelEditTitle}
                  className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 group">
                <h3 className="text-lg font-semibold text-gray-900">{deliverable.title}</h3>
                <button
                  onClick={handleStartEditTitle}
                  className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-100 rounded transition-all"
                  title="Edit title"
                >
                  <Edit3 className="w-4 h-4 text-gray-500" />
                </button>
              </div>
            )}
            <p className="text-sm text-gray-500">{deliverable.task_name}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-hidden flex">
          {isMediaContent ? (
            <>
              {/* Left: Media Preview (2/3) */}
              <div className="w-2/3 p-4 bg-gray-50 flex items-center justify-center overflow-auto">
                {isVideo ? (
                  <video
                    ref={videoRef}
                    src={deliverable.file_url}
                    controls
                    className="max-w-full max-h-full rounded-lg shadow-lg"
                  />
                ) : (
                  <img
                    src={deliverable.file_url}
                    alt={deliverable.title}
                    className="max-w-full max-h-full rounded-lg shadow-lg object-contain"
                  />
                )}
              </div>

              {/* Right: Comments (1/3) */}
              <div className="w-1/3 border-l border-gray-200 flex flex-col">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-gray-900">Comments</h4>
                  {canClearAll && comments.length > 0 && (
                    <button
                      onClick={handleClearAllComments}
                      className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1"
                    >
                      <Trash2 className="w-3 h-3" />
                      Clear All
                    </button>
                  )}
                </div>

                {/* Comments List */}
                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                  {comments.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-4">No comments yet</p>
                  ) : (
                    comments.map(comment => renderComment(comment))
                  )}
                </div>

                {/* Add Comment */}
                <div className="p-4 border-t border-gray-200">
                  {replyingTo && (
                    <div className="mb-2 text-xs text-gray-600 flex items-center justify-between bg-blue-50 p-2 rounded">
                      <span>Replying to comment...</span>
                      <button onClick={() => setReplyingTo(null)} className="text-blue-600 hover:text-blue-800">
                        Cancel
                      </button>
                    </div>
                  )}
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder={replyingTo ? "Write a reply..." : "Add a comment..."}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none text-sm"
                    rows="3"
                  />
                  <button
                    onClick={handleAddComment}
                    disabled={submitting || !newComment.trim()}
                    className="mt-2 w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      background: 'linear-gradient(to right, rgb(147, 51, 234), rgb(219, 39, 119))'
                    }}
                  >
                    <Send className="w-4 h-4" />
                    {submitting ? 'Sending...' : 'Send'}
                  </button>
                </div>
              </div>
            </>
          ) : (
            // Single column for non-media content
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Content Info */}
              <div className="p-6 border-b border-gray-200">
                {deliverable.description && (
                  <p className="text-sm text-gray-600 mb-4">{deliverable.description}</p>
                )}
                {deliverable.file_url && (
                  <a
                    href={deliverable.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 underline"
                  >
                    View File
                  </a>
                )}
              </div>

              {/* Comments Section */}
              <div className="flex-1 flex flex-col overflow-hidden">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-gray-900">Comments</h4>
                  {canClearAll && comments.length > 0 && (
                    <button
                      onClick={handleClearAllComments}
                      className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1"
                    >
                      <Trash2 className="w-3 h-3" />
                      Clear All
                    </button>
                  )}
                </div>

                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                  {comments.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-4">No comments yet</p>
                  ) : (
                    comments.map(comment => renderComment(comment))
                  )}
                </div>

                <div className="p-3 border-t border-gray-200">
                  {replyingTo && (
                    <div className="mb-2 text-xs text-gray-600 flex items-center justify-between bg-blue-50 p-2 rounded">
                      <span>Replying to comment...</span>
                      <button onClick={() => setReplyingTo(null)} className="text-blue-600 hover:text-blue-800">
                        Cancel
                      </button>
                    </div>
                  )}
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder={replyingTo ? "Write a reply..." : "Add a comment..."}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none text-sm"
                    rows="3"
                  />
                  <button
                    onClick={handleAddComment}
                    disabled={submitting || !newComment.trim()}
                    className="mt-2 w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      background: 'linear-gradient(to right, rgb(147, 51, 234), rgb(219, 39, 119))'
                    }}
                  >
                    <Send className="w-4 h-4" />
                    {submitting ? 'Sending...' : 'Send'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeliverableViewModal;
